import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Grid, Settings, BookOpen, TrendingUp, User as UserIcon, Plus, Library } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import useInstanceStore from '@/stores/instanceStore';
import useUserStore from '@/stores/userStore';
import { getAuthHeader, getToken as getStoredToken } from '../services/authToken';
import { useChapter } from '@/contexts/ChapterContext';
import InstanceGrid from './InstanceGrid';
import SettingsScreen from './SettingsScreen';

const UserProfileNew = ({ onBack, onNewInstance, onInstanceSelect,setCurrentTab,setShowSettings }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const globalUser = useUserStore((state) => state.user);
  const { selectedInstanceId, instances, setDemoInstances: setDemoInstancesInStore } = useInstanceStore();
  const { currentChapter, navigateToChapter, getChapterProgress, updateChapterProgress } = useChapter();
  const [activeTab, setActiveTab] = useState('library');
  const [profileData, setProfileData] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [learningMode, setLearningMode] = useState('open'); // 'narrative' or 'open'
  // const [showSettings, setShowSettings] = useState(false);

  // Demo Library state
  const [demoInstances, setDemoInstances] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState(null);
  // Use chapter context instead of local state

  // Debug: Log user data
  console.log('UserProfileNew - User data:', user);
  console.log('UserProfileNew - Global user data:', globalUser);

  // Load profile data
  useEffect(() => {
    if (user?.id) {
      loadProfileData();
      loadAnalytics();
    } else {
      // Use fallback data if no authenticated user
      console.log('No authenticated user, using fallback data');
      setProfileData({
        user: {
          id: 'demo-user',
          username: 'demo_user',
          full_name: 'Demo User',
          bio: 'Login to see your real learning progress.',
          avatar_url: null
        },
        stats: {
          instances: 12,
          total_chapters: 0,
          completed_chapters: 45,
          quizzes_taken: 0,
          average_quiz_score: 88,
          reading_streak: 0,
          total_reading_minutes: 0
        }
      });
      loadAnalytics();
      setLoading(false); // Fix: Set loading to false for fallback data
    }
  }, [user?.id]);

  // Update global user store when profile data is loaded
  useEffect(() => {
    if (profileData?.user) {
      const setUser = useUserStore.getState().setUser;
      setUser(profileData.user);
    }
  }, [profileData?.user]);

  // Load chapters when instance changes
  useEffect(() => {
    console.log('📚 useEffect triggered - selectedInstanceId:', selectedInstanceId, 'learningMode:', learningMode);
    if (selectedInstanceId) {
      console.log('📚 useEffect: Calling loadChapters()');
      loadChapters();
    } else {
      console.log('📚 useEffect: No selectedInstanceId, skipping loadChapters');
    }
  }, [selectedInstanceId, learningMode]);

  const loadProfileData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const userId = user?.id || 'demo-user';

      console.log('Loading profile data for user:', userId);

      // Fetch profile data
      const profileResponse = await fetch(`${backendUrl}/api/user/profile/${userId}`, {
        headers: getAuthHeader(),
      });

      // Fetch stats data
      const statsResponse = await fetch(`${backendUrl}/api/user/stats/${userId}`, {
        headers: getAuthHeader(),
      });

      if (profileResponse.ok && statsResponse.ok) {
        const profileData = await profileResponse.json();
        const statsData = await statsResponse.json();

        console.log('Profile data received:', profileData);
        console.log('Stats data received:', statsData);

        // Combine profile and stats data
        const combinedData = {
          user: profileData.user,
          stats: {
            instances: statsData.instances_count,
            total_chapters: statsData.total_chapters,
            completed_chapters: statsData.completed_chapters,
            quizzes_taken: statsData.quizzes_taken,
            average_quiz_score: statsData.average_quiz_score,
            reading_streak: statsData.reading_streak_days,
            total_reading_minutes: statsData.total_reading_minutes
          }
        };

        setProfileData(combinedData);
      } else {
        // Fallback to original profile data only if stats API fails
        const profileResponse = await fetch(`${backendUrl}/api/user/profile/${userId}`, {
          headers: getAuthHeader(),
        });

        if (profileResponse.ok) {
          const data = await profileResponse.json();
          console.log('Profile data received (fallback):', data);

          // Use the original stats but without followers/following
          setProfileData({
            user: data.user,
            stats: {
              instances: data.stats?.instances || 0,
              total_chapters: data.stats?.total_chapters || 0,
              completed_chapters: data.stats?.completed_chapters || 0,
              quizzes_taken: data.stats?.quizzes_taken || 0,
              average_quiz_score: data.stats?.average_quiz_score || 0,
              reading_streak: data.stats?.reading_streak_days || 0,
              total_reading_minutes: data.stats?.total_reading_minutes || 0
            }
          });
        } else {
          console.warn('Profile API failed, using fallback data');
          setProfileData({
            user: {
              id: userId,
              username: user?.username || 'demo_user',
              full_name: user?.full_name || 'Demo User',
              bio: 'Learning with ScrollEd',
              avatar_url: user?.avatar_url
            },
            stats: {
              instances: 12, // Placeholder
              total_chapters: 0,
              completed_chapters: 45, // Placeholder
              quizzes_taken: 0,
              average_quiz_score: 88, // Placeholder
              reading_streak: 0,
              total_reading_minutes: 0
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      // Fallback to mock data
      setProfileData({
        user: {
          id: user?.id || 'demo-user',
          username: user?.username || 'demo_user',
          full_name: user?.full_name || 'Demo User',
          bio: 'Learning with ScrollEd',
          avatar_url: user?.avatar_url
        },
        stats: {
          instances: 12,
          total_chapters: 0,
          completed_chapters: 45,
          quizzes_taken: 0,
          average_quiz_score: 88,
          reading_streak: 0,
          total_reading_minutes: 0
        }
      });
    } finally {
      setLoading(false); // Always set loading to false when done
    }
  };

  const loadChapters = async () => {
    console.log('🔍 loadChapters called with selectedInstanceId:', selectedInstanceId, 'learningMode:', learningMode);
    
    if (!selectedInstanceId) {
      console.warn('⚠️ loadChapters: No selectedInstanceId, returning early');
      return;
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const url = `${backendUrl}/api/user/chapters/${selectedInstanceId}?mode=${learningMode}`;
      console.log('🔗 loadChapters: Making API call to:', url);
      
      const response = await fetch(url, {
        headers: getAuthHeader(),
      });

      console.log('📡 loadChapters: Response status:', response.status, 'ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ loadChapters: Received data:', data);
        
        // Calculate real progress for each chapter using context
        const chaptersWithProgress = (data.chapters || []).map(chapter => {
          // Get progress from context or calculate from API data
          const contextProgress = getChapterProgress(chapter.chapter_number);
          const totalPosts = chapter.total_posts || contextProgress.total_posts || 0;
          const viewedPosts = chapter.viewed_posts || contextProgress.viewed_posts || 0;
          const realProgress = totalPosts > 0 ? Math.round((viewedPosts / totalPosts) * 100) : 0;
          
          // Update context with latest data
          if (totalPosts > 0) {
            updateChapterProgress(chapter.chapter_number, viewedPosts, totalPosts);
          }
          
          return {
            ...chapter,
            progress: realProgress, // Override with real progress
            total_posts: totalPosts,
            viewed_posts: viewedPosts
          };
        });
        
        console.log('✅ loadChapters: Setting chapters:', chaptersWithProgress);
        setChapters(chaptersWithProgress);
      } else {
        console.error('❌ loadChapters: API request failed with status:', response.status);
        const errorText = await response.text();
        console.error('❌ loadChapters: Error response:', errorText);
        setChapters([]);
      }
    } catch (error) {
      console.error('❌ loadChapters: Exception occurred:', error);
      // No hardcoded fallback - chapters should be generated during document processing
      // Display appropriate message to user instead
      console.warn('No chapters available - documents may still be processing or no documents uploaded');
      setChapters([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const userId = user?.id || 'demo-user';

      if (!userId || userId === 'demo-user') {
        // Use empty data immediately if no user
        setAnalytics({
          overview: {
            total_study_time: 0,
            total_posts_viewed: 0,
            total_quizzes_completed: 0,
            average_quiz_score: 0,
            streak_days: 0
          },
          instance_progress: []
        });
        return;
      }

      const response = await fetch(`${backendUrl}/api/user/analytics/${userId}`, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      // Fallback to empty analytics
      setAnalytics({
        overview: {
          total_study_time: 0,
          total_posts_viewed: 0,
          total_quizzes_completed: 0,
          average_quiz_score: 0,
          streak_days: 0
        },
        instance_progress: []
      });
    }
  };

  // Fetch demo instances
  const fetchDemoInstances = useCallback(async () => {
    if (demoLoading) return; // Prevent concurrent fetches

    try {
      setDemoLoading(true);
      setDemoError(null);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
      console.log('🔍 Fetching demo instances from:', `${backendUrl}/api/instances?demo=true`);

      const response = await fetch(`${backendUrl}/api/instances?demo=true`, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Demo instances loaded:', data);

        // Normalize demo instances to match InstanceSummary shape
        const normalizedDemos = (data.demoInstances || []).map(instance => ({
          id: instance.id,
          name: instance.name || 'Untitled Demo',
          description: instance.description || 'Demo instance',
          thumbnail: instance.cover_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop',
          cover_image: instance.cover_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=400&fit=crop',
          status: instance.status || 'ready',
          progress: instance.progress || 0,
          posts_count: instance.posts_count || 0,
          created_at: instance.created_at,
          updated_at: instance.updated_at,
          owner_id: instance.owner_id || instance.user_id,
          is_demo: true // Mark as demo
        }));

        setDemoInstances(normalizedDemos);
        setDemoInstancesInStore(normalizedDemos);
        setDemoError(null);
      } else {
        throw new Error(`Failed to fetch demo instances: ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error loading demo instances:', error);
      setDemoError(error.message || 'Failed to load demo library');
      setDemoInstances([]);
    } finally {
      setDemoLoading(false);
    }
  }, [demoLoading]);

  // Handle tab change
  const handleTabChange = useCallback((value) => {
    setActiveTab(value);

    // Always fetch fresh demo instances when switching to Demo Library tab
    if (value === 'demo-library') {
      console.log('🔄 Demo library: Fetching fresh data from server');
      fetchDemoInstances();
    }
  }, [fetchDemoInstances]);

  const handleChapterClick = (chapter) => {
    if (chapter.is_locked && learningMode === 'narrative') {
      toast.info('Complete previous chapters to unlock this one');
      return;
    }

    // Navigate to chapter using context
    navigateToChapter(chapter.chapter_number);
    
    // Navigate to chapter content in feed
    toast.success(`Navigating to ${chapter.title}`);
    
    // Switch to home/feed tab to show the chapter content
    if (onBack) {
      onBack(); // This should navigate back to the main app with the updated chapter
      
      // After navigation, scroll to the first post of this chapter
      setTimeout(() => {
        const chapterSelector = `[data-chapter="${chapter.chapter_number}"]`;
        const chapterElement = document.querySelector(chapterSelector);
        if (chapterElement) {
          chapterElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // Fallback: scroll to top if specific chapter not found
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  };

  // Merge global user data with fetched profile data
  const displayUser = {
    ...(profileData?.user || {}),
    ...(globalUser || {})
  };

  if (loading || !profileData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-32 mx-auto mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-24 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-bold text-lg">{displayUser.username}</h1>
            <p className="text-sm text-gray-600">{profileData.stats.instances} {t('profile.stats_libraries')}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => {setShowSettings(true);setCurrentTab('profile');}}>
          <Settings className="w-5 h-5" />
        </Button>
      </div>

      {/* Profile Info */}
      <div className="p-4">
        <div className="flex items-start gap-4 mb-4">
          <Avatar className="w-20 h-20">
            <AvatarImage src={displayUser.avatar_url} alt={displayUser.full_name} />
            <AvatarFallback className="text-2xl">{displayUser.full_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="font-bold text-xl">{displayUser.full_name}</h2>
            </div>
            <p className="text-gray-600 text-sm mb-3">@{displayUser.username}</p>
            
            <div className="flex gap-6 mb-3">
              <div className="text-center">
                <p className="font-bold text-lg">
                  {profileData.stats.instances > 0 ? profileData.stats.instances : 12}
                </p>
                <p className="text-gray-600 text-xs">{t('profile.stats_libraries')}</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-lg">
                  {profileData.stats.completed_chapters > 0 ? profileData.stats.completed_chapters : 45}
                </p>
                <p className="text-gray-600 text-xs">{t('profile.stats_chapters')}</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-lg">
                  {profileData.stats.average_quiz_score > 0 ? profileData.stats.average_quiz_score : 88}%
                </p>
                <p className="text-gray-600 text-xs">{t('profile.stats_score')}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button className="flex-1" onClick={onNewInstance}>
                <Plus className="w-4 h-4 mr-2" />
                {t('profile.new_library_item')}
              </Button>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-gray-800 text-sm leading-relaxed">{displayUser.bio}</p>
          <div className="mt-2 flex gap-2">
            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
              📚 {t('profile.learner_badge')}
            </span>
            <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
              🔥 {t('profile.streak_badge', { count: analytics?.overview.streak_days || 7 })}
            </span>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full justify-center border-t border-gray-100 bg-white rounded-none overflow-x-auto">
          <TabsTrigger value="library" className="flex-1 flex items-center gap-2">
            <Grid className="w-4 h-4" />
            <span className="hidden sm:inline">{t('profile.library_tab')}</span>
          </TabsTrigger>
          <TabsTrigger value="demo-library" className="flex-1 flex items-center gap-2">
            <Library className="w-4 h-4" />
            <span className="hidden sm:inline">{t('profile.demo_library_tab')}</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex-1 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">{t('profile.analytics_tab')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Chapters tab removed - now available in main Chapters navigation */}

        <TabsContent value="library" className="mt-0">
          <InstanceGrid
            onNewInstance={onNewInstance}
            onInstanceSelect={onInstanceSelect}
          />
        </TabsContent>

        <TabsContent value="demo-library" className="mt-0">
          <section className="p-2 sm:p-4">
            {/* Header with helper text */}
            <div className="mb-3 sm:mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('demo.title')}</h2>
              <p className="text-sm text-gray-600">
                {t('demo.description')}
              </p>
            </div>

            {/* Loading state */}
            {demoLoading && (
              <div className="grid grid-cols-3 gap-1 sm:gap-2">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="aspect-square bg-gray-200 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            )}

            {/* Error state */}
            {demoError && !demoLoading && (
              <div className="border border-red-200 bg-red-50 rounded-lg p-6 text-center">
                <p className="font-medium text-red-900 mb-2">Unable to load demo library</p>
                <p className="text-sm text-red-700 mb-4">{demoError}</p>
                <Button
                  variant="outline"
                  onClick={fetchDemoInstances}
                  size="sm"
                >
                  Try Again
                </Button>
              </div>
            )}

            {/* Empty state */}
            {!demoLoading && !demoError && demoInstances.length === 0 && (
              <div className="border border-gray-200 bg-gray-50 rounded-lg p-6 text-center">
                <p className="font-medium text-gray-900 mb-2">No demo instances yet</p>
                <p className="text-sm text-gray-600">
                  We are refreshing the sample content. Please try again later.
                </p>
              </div>
            )}

            {/* Demo instances grid */}
            {!demoLoading && !demoError && demoInstances.length > 0 && (
              <InstanceGrid
                instancesOverride={demoInstances}
                readOnly={true}
                onInstanceSelect={onInstanceSelect}
                onRetry={fetchDemoInstances}
              />
            )}
          </section>
        </TabsContent>

        <TabsContent value="analytics" className="mt-0">
          <div className="p-4 space-y-6">
            <h3 className="font-semibold text-gray-900">Learning Analytics</h3>
            
            {/* Stats Overview */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {analytics?.overview?.total_study_time > 0 ? analytics.overview.total_study_time : 42}h
                </div>
                <div className="text-sm text-gray-600">Total Study Time</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {analytics?.overview?.average_quiz_score > 0 ? analytics.overview.average_quiz_score : 78}%
                </div>
                <div className="text-sm text-gray-600">Quiz Average</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {analytics?.overview?.total_posts_viewed > 0 ? analytics.overview.total_posts_viewed : 350}
                </div>
                <div className="text-sm text-gray-600">Posts Viewed</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {analytics?.overview?.total_quizzes_completed > 0 ? analytics.overview.total_quizzes_completed : 85}
                </div>
                <div className="text-sm text-gray-600">Quizzes Done</div>
              </div>
            </div>

            {/* Progress by Library Item */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Library Progress</h4>
              <div className="space-y-3">
                {(analytics?.instance_progress || []).map((progress, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium text-gray-900">{progress.instance_name}</h5>
                      <span className="text-sm text-gray-600">{progress.progress_percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${progress.progress_percentage}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{progress.chapters_completed}/{progress.total_chapters} chapters</span>
                      <span>{Math.floor((progress.time_spent_minutes || 0) / 60)}h {(progress.time_spent_minutes || 0) % 60}m</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Achievements */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Recent Achievements</h4>
              <div className="space-y-2">
                {(analytics?.achievements || []).slice(0, 3).map((achievement, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                    <div className="text-2xl">🏆</div>
                    <div>
                      <h5 className="font-medium text-gray-900">{achievement.name}</h5>
                      <p className="text-sm text-gray-600">{achievement.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Settings Screen Modal Overlay */}
     
    </div>
  );
};

export default UserProfileNew;
